import { SoftCard } from './ui/modern-card';
import { Button } from './ui/button';
import { Users, MapPin, Plus, Edit } from 'lucide-react';
import { Zone } from '../types';

interface ZonesListProps {
  zones: Zone[];
  onZoneClick: (zone: Zone) => void;
  selectedZoneId?: string;
  hoveredZoneId?: string;
  onZoneHover: (zoneId: string | undefined) => void;
  onViewDetail: (zone: Zone) => void;
  onAddZone?: () => void;
}

export default function ZonesList({ 
  zones, 
  onZoneClick, 
  selectedZoneId, 
  hoveredZoneId,
  onZoneHover,
  onViewDetail,
  onAddZone
}: ZonesListProps) {

  const getContactsCount = (zone: Zone) => {
    return zone.contacts?.length || 0;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-100/50 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">Zones de surveillance</h2>
          <span className="text-sm text-muted-foreground">
            {zones.length} zone{zones.length > 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Bouton d'ajout */}
        {onAddZone && (
          <Button
            onClick={onAddZone}
            size="sm"
            className="gap-2 w-full bg-gray-900 hover:bg-gray-800 text-white"
          >
            <Plus className="w-4 h-4" />
            Ajouter une zone
          </Button>
        )}
      </div>

      {/* Liste des zones */}
      <div className="flex-1 overflow-y-auto">
        {zones.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Aucune zone configurée</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {zones.map((zone) => (
              <SoftCard
                key={zone.id}
                className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selectedZoneId === zone.id 
                    ? 'ring-2 ring-blue-500/50 shadow-md bg-blue-50/50' 
                    : hoveredZoneId === zone.id
                    ? 'ring-1 ring-gray-300 shadow-md bg-gray-50/50'
                    : 'hover:shadow-md hover:bg-white/90'
                }`}
                onClick={() => onZoneClick(zone)}
                onMouseEnter={() => onZoneHover(zone.id)}
                onMouseLeave={() => onZoneHover(undefined)}
              >
                <div className="space-y-3">
                  {/* En-tête de la zone */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium leading-tight line-clamp-2">
                        {zone.name}
                      </h3>
                    </div>
                  </div>


                  {/* Informations sur les contacts */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span>
                        {getContactsCount(zone)} contact{getContactsCount(zone) > 1 ? 's' : ''}
                      </span>
                    </div>
                    {onAddZone && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetail(zone);
                        }}
                        className="h-7 px-2 text-xs hover:bg-gray-100 border border-gray-300"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Modifier
                      </Button>
                    )}
                  </div>
                </div>
              </SoftCard>
            ))}
          </div>
        )}
      </div>

      {/* Légende */}
      <div className="border-t border-gray-100/50 p-4 pb-20 bg-gray-50/30">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>• Survolez pour mettre en surbrillance sur la carte</p>
          <p>• Cliquez pour centrer la carte sur la zone</p>
          {onAddZone && <p>• Bouton "Modifier" pour éditer les détails</p>}
        </div>
      </div>
    </div>
  );
}